import React, { useRef, useState, useEffect } from 'react';
import * as Yup from 'yup';
import useI18n from '@/hooks/useI18n';
import { FormDataForCreateCampaign } from '@/types/interfaces';
import { Button } from '../Button';
import ToolTip from '../ToolTip';
import { InfoIcon } from '@/svg';
import { getActivePlatforms } from '@/utils/socialplatformUtils';

interface CreateCampaignStep5Props {
  formData: FormDataForCreateCampaign;
  handleChange: (newValues: Partial<FormDataForCreateCampaign>) => void;
  onNext: (values: FormDataForCreateCampaign) => void;
  onPrevious: () => void;
}

const CreateCampaignStep5: React.FC<CreateCampaignStep5Props> = ({
  formData,
  handleChange,
  onNext,
  onPrevious,
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isFocusedTitle, setIsFocusedTitle] = useState<boolean>(false);
  const [hashtagInput, setHashtagInput] = useState<string>('');
  const [instagramTagInput, setInstagramTagInput] = useState<string>('');
  const [tiktokTagInput, setTiktokTagInput] = useState<string>('');
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormDataForCreateCampaign, string>>
  >({});
  const { t } = useI18n();
  useEffect(() => {
    if (formData.contentTitle !== '') {
      handleChange({ contentTitle: '' });
    }
  }, []);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const hashtagInputRef = useRef<HTMLInputElement>(null);
  const instagramTagInputRef = useRef<HTMLInputElement>(null);
  const tiktokTagInputRef = useRef<HTMLInputElement>(null);

  const activePlatforms = getActivePlatforms(formData);

  const validationSchema = Yup.object({
    // contentTitle: Yup.string().required(String(t('common.mandatoryField'))),
    contentDescription: Yup.string()
      .required(String(t('common.mandatoryField')))
      .max(300, String(t('common.maxCharacters'))),
    hashtags: Yup.array().min(
      1,
      String(t('campaign.validation.hashtagRequired')),
    ),
    instagramTags: activePlatforms.instagram
      ? Yup.array().min(
          1,
          String(t('campaign.validation.instagramTagRequired')),
        )
      : Yup.array().notRequired(),
    tiktokTags: activePlatforms.tiktok
      ? Yup.array().min(1, String(t('campaign.validation.tiktokTagRequired')))
      : Yup.array().notRequired(),
  });

  const handleHashtagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHashtagInput(e.target.value);
  };

  const handleHashtagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Tab') {
      e.preventDefault();
      addHashtag();
    }
  };

  const handleHashtagBlur = () => {
    addHashtag();
  };

  const addHashtag = () => {
    const trimmedInput = hashtagInput.trim();
    if (trimmedInput) {
      const newHashtag = trimmedInput.startsWith('#')
        ? trimmedInput
        : `#${trimmedInput}`;
      const updatedHashtags = [...(formData.hashtags || []), newHashtag];
      handleChange({ hashtags: updatedHashtags });
      setHashtagInput('');
    }
  };

  // Space already commits a typed tag, but a pasted "@one @two" lands in the
  // input whole — split it so each handle becomes its own tag.
  const toTags = (input: string) =>
    input
      .split(/[\s,]+/)
      .filter(Boolean)
      .map((tag) => (tag.startsWith('@') ? tag : `@${tag}`));

  const handleRemoveInstagramTag = (tag: string) => {
    const updatedTags = (formData.instagramTags || []).filter((t) => t !== tag);
    handleChange({ instagramTags: updatedTags });
  };

  const handleRemoveTiktokTag = (tag: string) => {
    const updatedTags = (formData.tiktokTags || []).filter((t) => t !== tag);
    handleChange({ tiktokTags: updatedTags });
  };

  const handleRemoveHashtag = (hashtag: string) => {
    const updatedHashtags = (formData.hashtags || []).filter(
      (h) => h !== hashtag,
    );
    handleChange({ hashtags: updatedHashtags });
  };

  const handleInstagramTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstagramTagInput(e.target.value);
  };
  const handleInstagramTagKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Tab') {
      e.preventDefault();
      addInstagramTag();
    }
  };
  const handleInstagramTagBlur = () => {
    addInstagramTag();
  };
  const addInstagramTag = () => {
    const newTags = toTags(instagramTagInput);
    if (newTags.length) {
      handleChange({
        instagramTags: [...(formData.instagramTags || []), ...newTags],
      });
    }
    setInstagramTagInput('');
  };

  const handleTiktokTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTiktokTagInput(e.target.value);
  };
  const handleTiktokTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Tab') {
      e.preventDefault();
      addTiktokTag();
    }
  };
  const handleTiktokTagBlur = () => {
    addTiktokTag();
  };
  const addTiktokTag = () => {
    const newTags = toTags(tiktokTagInput);
    if (newTags.length) {
      handleChange({
        tiktokTags: [...(formData.tiktokTags || []), ...newTags],
      });
    }
    setTiktokTagInput('');
  };

  const handleContentDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;

    // update form data
    handleChange({ contentDescription: value });

    // live validation
    setErrors((prev) => ({
      ...prev,
      contentDescription: validateContentDescription(value),
    }));
  };

  const validateContentDescription = (text: string) => {
    if (text.length > 300) {
      return String(t('common.maxCharacters'));
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      onNext({
        ...formData,
        contentTitle: '',
      });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Partial<
          Record<keyof FormDataForCreateCampaign, string>
        > = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof FormDataForCreateCampaign] =
              error.message;
          }
        });
        setErrors(newErrors);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      className="flex flex-col flex-1 min-h-0"
    >
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
        <h3 className="text-base font-semibold text-[#172B4D]">
          {t('common.tags&Captions')}{' '}
          <ToolTip text={String(t(`common.step5Tooltip`))} position="top">
            <span>ⓘ</span>
          </ToolTip>
        </h3>

        <div className="flex gap-4 items-start">
          <div className="w-2/5 flex flex-col gap-4">
            {/* <div>
            <div className="relative">
              <input
                ref={titleRef}
                name="contentTitle"
                onFocus={() => setIsFocusedTitle(true)}
                onBlur={() => setIsFocusedTitle(false)}
                onClick={() => {
                  titleRef.current?.focus();
                  setIsFocusedTitle(true);
                }}
                type="text"
                value={formData.contentTitle}
                onChange={(e) => handleChange({ contentTitle: e.target.value })}
                className="block w-full border border-[#B3B3B2] py-3 px-4  [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder-[#B3B3B2] overflow-ellipsis whitespace-nowrap rounded-[32px] bg-white disabled:border-[#B3B3B2]  disabled:bg-[#F4F4F4] disabled:text-[#B3B3B2] outline-none focus:border focus:border-[#113E53] text-[#113E53]  text-base leading-[22px]"
                aria-label="contentTitle"
                placeholder={
                  isFocusedTitle ? '' : String(t('common.contentTitle'))
                }
              />
              {!formData.contentTitle && !isFocusedTitle && (
                <div className="absolute left-[112px] top-[12px] text-sm text-gray-400 pointer-events-auto">
                  <ToolTip
                    text={String(t('common.titleToUseInThePost'))}
                    position="top"
                  >
                    <span className="text-[#B3B3B2] text-base">ⓘ</span>
                  </ToolTip>
                </div>
              )}
              {errors.contentTitle && (
                <div className="text-red-600 text-sm mt-1">
                  {errors.contentTitle}
                </div>
              )}
            </div>
          </div> */}
            <div>
              <div className="relative">
                <div
                  className={`flex flex-wrap items-start gap-2 border border-[#B3B3B2]
    ${formData.hashtags.length >= 3 ? 'rounded-[24px]' : 'rounded-full'}
    py-3 px-4 min-h-[40px]
    w-full bg-white outline-none
    text-[#113E53] text-base leading-[22px] placeholder-[#B3B3B2]`}
                  style={{
                    maxHeight: '112px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE / Edge
                  }}
                >
                  {(formData.hashtags || []).map((hashtag, index) => (
                    <div
                      key={index}
                      className="flex flex-wrap items-center bg-blue-100 text-[#172B4D] text-sm font-medium px-2 py-1 rounded-lg"
                    >
                      <span className="max-w-[200px] truncate bg-blue-100 px-2 py-1 rounded">
                        {hashtag}
                      </span>
                      <button
                        onClick={() => handleRemoveHashtag(hashtag)}
                        className="ml-1 text-gray-600 hover:text-gray-800"
                        aria-label={String(
                          t('campaign.labels.removeHashtag', { hashtag }),
                        )}
                        type="button"
                      >
                        <img
                          src="/assets/fi_close.svg"
                          alt={String(t('campaign.altTexts.remove'))}
                          className="h-[16px] w-[16px]"
                        />
                      </button>
                    </div>
                  ))}
                  <input
                    ref={hashtagInputRef}
                    type="text"
                    value={hashtagInput}
                    onChange={handleHashtagInput}
                    onKeyDown={handleHashtagKeyDown}
                    onBlur={handleHashtagBlur}
                    placeholder={
                      formData.hashtags?.length
                        ? ''
                        : String(t('campaign.placeholders.addHashtags'))
                    }
                    className="flex-grow border-none focus:outline-none placeholder-[#B3B3B2]"
                    aria-label="hashtags"
                  />
                </div>
                {errors.hashtags && (
                  <div className="text-red-600 text-sm mt-1">
                    {errors.hashtags}
                  </div>
                )}
              </div>
            </div>
            {activePlatforms.instagram && (
              <div>
                <div className="relative">
                  <ToolTip
                    text={String(t('common.instagramTagsLabel'))}
                    position="top"
                    className="w-full align-top tooltip-nowrap"
                  >
                    <div
                      className={`flex flex-wrap items-start gap-2 border border-[#B3B3B2]
    ${formData.instagramTags.length >= 3 ? 'rounded-[24px]' : 'rounded-full'}
    py-3 px-4 min-h-[40px]
    w-full bg-white outline-none
    text-[#113E53] text-base leading-[22px] placeholder-[#B3B3B2]`}
                      style={{
                        maxHeight: '112px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      {(formData.instagramTags || []).map((tag, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap items-center bg-blue-100 text-[#172B4D] text-sm font-medium px-2 py-1 rounded-lg"
                        >
                          <span className="max-w-[200px] truncate bg-blue-100 px-2 py-1 rounded">
                            {tag}
                          </span>
                          <button
                            onClick={() => handleRemoveInstagramTag(tag)}
                            type="button"
                            className="ml-1 text-gray-600 hover:text-gray-800"
                            aria-label={String(
                              t('campaign.labels.removeInstagramTag', { tag }),
                            )}
                          >
                            <img
                              src="/assets/fi_close.svg"
                              alt={String(t('campaign.altTexts.remove'))}
                              className="h-[16px] w-[16px]"
                            />
                          </button>
                        </div>
                      ))}
                      <input
                        ref={instagramTagInputRef}
                        type="text"
                        value={instagramTagInput}
                        onChange={handleInstagramTagInput}
                        onKeyDown={handleInstagramTagKeyDown}
                        onBlur={handleInstagramTagBlur}
                        placeholder={
                          formData.instagramTags?.length
                            ? ''
                            : String(
                                t('campaign.placeholders.addInstagramTags'),
                              )
                        }
                        className="flex-grow border-none focus:outline-none placeholder-[#B3B3B2]"
                        aria-label="instagramTags"
                      />
                    </div>
                  </ToolTip>
                  {errors.instagramTags && (
                    <div className="text-red-600 text-sm mt-1">
                      {errors.instagramTags}
                    </div>
                  )}
                </div>
              </div>
            )}
            {activePlatforms.tiktok && (
              <div>
                <div className="relative">
                  <ToolTip
                    text={String(t('common.tiktokTagsLabel'))}
                    position="top"
                    className="w-full align-top tooltip-nowrap"
                  >
                    <div
                      className={`flex flex-wrap items-start gap-2 border border-[#B3B3B2]
    ${formData.tiktokTags.length >= 3 ? 'rounded-[24px]' : 'rounded-full'}
    py-3 px-4 min-h-[40px]
    w-full bg-white outline-none
    text-[#113E53] text-base leading-[22px] placeholder-[#B3B3B2]`}
                      style={{
                        maxHeight: '112px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      {(formData.tiktokTags || []).map((tag, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap items-center bg-blue-100 text-[#172B4D] text-sm font-medium px-2 py-1 rounded-lg"
                        >
                          <span className="max-w-[200px] truncate bg-blue-100 px-2 py-1 rounded">
                            {tag}
                          </span>
                          <button
                            onClick={() => handleRemoveTiktokTag(tag)}
                            type="button"
                            className="ml-1 text-gray-600 hover:text-gray-800"
                            aria-label={String(
                              t('campaign.labels.removeTiktokTag', { tag }),
                            )}
                          >
                            <img
                              src="/assets/fi_close.svg"
                              alt={String(t('campaign.altTexts.remove'))}
                              className="h-[16px] w-[16px]"
                            />
                          </button>
                        </div>
                      ))}
                      <input
                        ref={tiktokTagInputRef}
                        type="text"
                        value={tiktokTagInput}
                        onChange={handleTiktokTagInput}
                        onKeyDown={handleTiktokTagKeyDown}
                        onBlur={handleTiktokTagBlur}
                        placeholder={
                          formData.tiktokTags?.length
                            ? ''
                            : String(t('campaign.placeholders.addTiktokTags'))
                        }
                        className="flex-grow border-none focus:outline-none placeholder-[#B3B3B2]"
                        aria-label="tiktokTags"
                      />
                    </div>
                  </ToolTip>
                  {errors.tiktokTags && (
                    <div className="text-red-600 text-sm mt-1">
                      {errors.tiktokTags}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="w-3/5">
            <div className="relative">
              <textarea
                ref={textareaRef}
                name="contentDescription"
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onClick={() => {
                  textareaRef.current?.focus();
                  setIsFocused(true);
                }}
                value={formData.contentDescription}
                onChange={handleContentDescriptionChange}
                rows={4}
                aria-label={String(t('campaign.labels.contentDescription'))}
                placeholder={
                  isFocused ? '' : String(t('common.contentDescription'))
                }
                className="block w-full border border-[#B3B3B2] py-3 px-4 h-[176px] rounded-[24px] placeholder-[#B3B3B2] bg-white outline-none focus:border focus:border-[#113E53] text-[#113E53] text-base leading-[22px] resize-none whitespace-pre-wrap overflow-auto"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              />

              {/* {!formData?.contentDescription && !isFocused && (
              <div className="absolute right-4 top-3 pointer-events-auto">
                <ToolTip
                  text={String(t(`common.descriptionToIncludeInThePost`))}
                  position="left"
                  className="tooltip-wide"
                >
                  <InfoIcon className="w-5 h-5 cursor-pointer text-[#94A3B8]" />
                </ToolTip>
              </div>
            )} */}
              {errors.contentDescription && (
                <div className="text-red-600 text-sm mt-1">
                  {errors.contentDescription}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white flex justify-end w-full shrink-0">
        <div className="flex gap-4 w-full p-6 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={onPrevious}
            className="w-[122px]"
          >
            {t('common.previous')}
          </Button>
          <Button type="submit" className="w-[122px]">
            {t('common.next')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateCampaignStep5;
